import type { Session } from "../../chat/types";

export interface RecencyBucket { label: string; sessions: Session[]; }

const DAY = 24 * 3600_000;

/** Bucket sessions by `updated_at` recency relative to `now` (ms epoch).
 *  Buckets are returned in fixed order; empty buckets are omitted; within a
 *  bucket sessions are most-recent-first. Invalid/missing timestamps fall
 *  into "Older". Pure (now injected) for testability. */
export function groupByRecency(sessions: Session[], now: number): RecencyBucket[] {
  const order = ["Today", "Yesterday", "Previous 7 days", "Previous 30 days", "Older"] as const;
  const map = new Map<string, Array<{ s: Session; t: number }>>();
  for (const label of order) map.set(label, []);

  // Day boundaries based on `now`'s UTC midnight.
  const d = new Date(now);
  const todayMs = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const yesterdayMs = todayMs - DAY;
  const week = todayMs - 7 * DAY;
  const month = todayMs - 30 * DAY;

  for (const s of sessions) {
    const t = Date.parse(s.updated_at);
    let label: string;
    if (Number.isNaN(t)) label = "Older";
    else if (t >= todayMs) label = "Today";
    else if (t >= yesterdayMs) label = "Yesterday";
    else if (t >= week) label = "Previous 7 days";
    else if (t >= month) label = "Previous 30 days";
    else label = "Older";
    map.get(label)!.push({ s, t: Number.isNaN(t) ? -Infinity : t });
  }

  const out: RecencyBucket[] = [];
  for (const label of order) {
    const arr = map.get(label)!;
    if (!arr.length) continue;
    arr.sort((a, b) => b.t - a.t); // most recent first
    out.push({ label, sessions: arr.map((x) => x.s) });
  }
  return out;
}
